import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
    ArrowUpCircle,
    ArrowRightLeft,
    ShoppingCart,
    ArrowDownCircle,
    Wallet,
    TrendingUp,
} from 'lucide-react';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import StatusBadge from '../../../component/wallet/StatusBadge';
import LoadingSpinner from '../../../component/wallet/LoadingSpinner';
import EmptyState from '../../../component/wallet/EmptyState';
import { useWallets } from '../../../Hooks/wallet/usewallets';
import { formatDate, formatRelativeTime } from '../../../lib/wallet/utils';

const DashboardPage = () => {
    const { sidebar } = useContext(AppContext);
    const navigate = useNavigate();
    const { loading, fetchWallets, fetchTransactions } = useWallets();
    const [wallets, setWallets] = useState([]);
    const [recentTxns, setRecentTxns] = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const wRes = await fetchWallets();
                const w = wRes?.data || [];
                setWallets(w);

                // Fetch recent transactions from first wallet
                if (w.length > 0) {
                    const tRes = await fetchTransactions({ wallet_id: w[0].id, page: 1, limit: 5 });
                    setRecentTxns(tRes?.data?.transactions || []);
                }
            } catch {
                // errors handled by interceptor
            }
        };
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading && wallets.length === 0) return <LoadingSpinner fullPage />;

    const quickActions = [
        { label: 'Top Up', icon: ArrowUpCircle, to: '/wallet/topup', variant: 'success' },
        { label: 'Transfer', icon: ArrowRightLeft, to: '/wallet/transfer', variant: 'primary' },
        // { label: 'Purchase', icon: ShoppingCart, to: '/wallet/purchase', variant: 'warning' },
        { label: 'Withdraw', icon: ArrowDownCircle, to: '/wallet/withdraw', variant: 'danger' },
    ];

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Dashboard</h6>
            </div>
                <div className="fade-in">
            

            {/* Balance Cards */}
            <Row className="g-3 mb-4">
                {wallets.length === 0 ? (
                    <Col xs={12}>
                        <EmptyState title="No wallets" description="You don't have any wallets yet." />
                    </Col>
                ) : (
                    wallets.map((w) => (
                        <Col xs={12} sm={6} lg={4} xl={3} key={w.id}>
                            <Card className="wallet-card h-100 border-0 shadow-sm">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <Wallet size={20} className="text-primary" />
                                            <span className="small fw-semibold text-muted text-uppercase">
                                                {w.currency_code}
                                            </span>
                                        </div>
                                        <StatusBadge status={w.wallet_type} />
                                    </div>
                                    <div className="mb-2">
                                        <CurrencyDisplay
                                            amount={w.balance}
                                            currencyCode={w.currency_code}
                                            currencySymbol={w.currency_symbol}
                                            className="fs-4"
                                        />
                                    </div>
                                    <div className="mt-auto d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                            {w.wallet_type === 'reward' && w.expires_at
                                                ? `Expires: ${formatDate(w.expires_at, 'MMM dd, yyyy')}`
                                                : w.is_active
                                                    ? 'Active'
                                                    : 'Inactive'}
                                        </small>
                                        <TrendingUp size={14} className="text-success" />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))
                )}
            </Row>

            {/* Quick Actions */}
            <h6 className="fw-bold mb-3">Quick Actions</h6>
            <Row className="g-2 mb-4">
                {quickActions.map((a) => (
                    <Col xs={6} sm={3} key={a.label}>
                        <Button
                            variant={a.variant}
                            className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                            onClick={() => navigate(a.to)}
                        >
                            <a.icon size={18} />
                            {a.label}
                        </Button>
                    </Col>
                ))}
            </Row>

            {/* Recent Transactions */}
            <h6 className="fw-bold mb-3">Recent Transactions</h6>
            {recentTxns.length === 0 ? (
                <EmptyState title="No transactions yet" description="Your recent transactions will appear here." />
            ) : (
                <Card className="border-0 shadow-sm">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Description</th>
                                    <th>Date</th>
                                    <th className="text-end">
          <Button 
            className="btn btn-sm text-white btn-outline-primary" 
            onClick={() => navigate('/wallet/transactions')}
          >
            View All
          </Button>
        </th>
                                </tr>
                            </thead>
                          
                            <tbody>
                                {recentTxns.map((txn) => (
                                    <tr key={txn.id}>
                                        <td><StatusBadge status={txn.type} /></td>
                                        <td>
                                            <CurrencyDisplay amount={txn.amount} currencyCode="INR" />
                                        </td>
                                        <td className="text-muted small">{txn.description || '—'}</td>
                                        <td className="text-muted small">{formatRelativeTime(txn.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
        </div>
        </div>
    );
};

export default DashboardPage;
